import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
// import { persist } from "zustand/middleware"; // Remove persist
import { dbHelpers } from "../lib/db";
import { Track } from "../types";
import { useLocalLibraryStore } from "./localLibraryStore";

interface LocalFilesState {
  files: (Track & { blobData?: ArrayBuffer })[];
  fileBlobs: Record<string, string>;
  initialized: boolean;
  loadFilesFromDB: (onLoadCallback?: (files: Track[]) => void) => Promise<void>;
  initialize: () => Promise<void>;
  addFile: (file: File, onAddCallback?: (track: Track) => void) => Promise<void>;
  removeFile: (id: string, onRemoveCallback?: (id: string) => void) => void;
  getFiles: () => (Track & { blobData?: ArrayBuffer })[];
  getFileUrl: (id: string) => string | undefined;
  cleanup: () => void;
}

export const useLocalFilesStore = create<LocalFilesState>((set, get) => ({
  files: [],
  fileBlobs: {},
  initialized: false,

  loadFilesFromDB: async (onLoadCallback) => {
    if (get().initialized) return;

    console.log("Loading files from local files store...");

    try {
      const files = await dbHelpers.getAllTracks();
      const blobs: Record<string, string> = {};

      console.log("Found", files.length, "files in database");

      for (const file of files) {
        if (file.blobData) {
          const blob = new Blob([file.blobData], { type: "audio/mpeg" });
          blobs[file.id] = URL.createObjectURL(blob);
          file.url = blobs[file.id];
        }
      }

      set({ files, fileBlobs: blobs, initialized: true });

      // Remove circular dependency - sync will happen via effect
      // useLocalLibraryStore.getState().syncLocalFiles(files);

      if (onLoadCallback) {
        onLoadCallback(files);
      }

      console.log("Local files store initialized successfully with", files.length, "files");
    } catch (error) {
      console.error("Error loading files from DB:", error);
      set({ initialized: true }); // Always set initialized to prevent infinite loading
      throw error;
    }
  },

  initialize: async () => {
    await get().loadFilesFromDB();
  },

  addFile: async (file: File, onAddCallback) => {
    if (!file.type.startsWith("audio/")) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: file.type });
      const url = URL.createObjectURL(blob);

      const audio = new Audio();

      await new Promise((resolve, reject) => {
        const onLoad = () => {
          audio.removeEventListener("loadedmetadata", onLoad);
          audio.removeEventListener("error", onError);
          resolve(null);
        };
        const onError = (e: ErrorEvent) => {
          audio.removeEventListener("loadedmetadata", onLoad);
          audio.removeEventListener("error", onError);
          reject(e);
        };
        audio.addEventListener("loadedmetadata", onLoad);
        audio.addEventListener("error", onError);
        audio.src = url;
      });

      const id = uuidv4();
      const track: Track & { blobData: ArrayBuffer } = {
        id,
        title: file.name.replace(/\.[^/.]+$/, ""),
        artist: "Unknown Artist",
        duration: audio.duration,
        url: url,
        source: "local",
        fileSize: file.size,
        blobData: arrayBuffer,
      };

      await dbHelpers.addTrack(track);

      set((state) => ({
        files: [...state.files, track],
        fileBlobs: {
          ...state.fileBlobs,
          [id]: url,
        },
      }));

      useLocalLibraryStore.getState().addLocalTrack(track);

      if (onAddCallback) {
        onAddCallback(track);
      }

      audio.src = "";
    } catch (error) {
      console.error("Error adding file:", error);
      throw error;
    }
  },

  removeFile: async (id: string, onRemoveCallback) => {
    try {
      await dbHelpers.deleteTrack(id);

      set((state) => {
        const url = state.fileBlobs[id];
        if (url) {
          URL.revokeObjectURL(url);
        }

        const { [id]: _, ...remainingBlobs } = state.fileBlobs;
        return {
          files: state.files.filter((file) => file.id !== id),
          fileBlobs: remainingBlobs,
        };
      });

      useLocalLibraryStore.getState().removeLocalTrack(id);

      if (onRemoveCallback) {
        onRemoveCallback(id);
      }
    } catch (error) {
      console.error("Error removing file:", error);
      throw error;
    }
  },

  getFiles: () => get().files,

  getFileUrl: (id: string) => get().fileBlobs[id],

  cleanup: () => {
    Object.values(get().fileBlobs).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    set({ files: [], fileBlobs: {}, initialized: false });
  },
}));

// Initialize the local files store automatically
console.log("Initializing local files store...");
useLocalFilesStore
  .getState()
  .loadFilesFromDB()
  .then(() => {
    console.log("Local files store initialized successfully");
  })
  .catch((error) => {
    console.error("Local files store initialization failed:", error);
  });

useLocalFilesStore.subscribe((state) => {
  if (state.initialized) {
    state.files.forEach((file) => {
      dbHelpers.addTrack(file).catch(console.error);
    });
  }
});
